import { useCallback, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { routerPaths } from "@/ui/router";
import { createGeneratedGLTab, type TGLFileDetails, type TGLLineItem } from "@/ui/views";
import type { TAsyncState } from "@/store";
import type { TApiLineItemFilterOptions, TApiLineItemQuery } from "@/apis/proda_finance/types";
import type { TCompositionDeps } from "./types";

export const GL_LINES_PAGE_SIZE = 100;

export const wireGLLines = ({ api }: TCompositionDeps) => {
    const GeneratedGLTabView = createGeneratedGLTab();

    const useGeneratedGLUseCases = () => {
        const [glLinesState, setGLLinesState] = useState<{
            status: "idle" | "pending" | "fulfilled" | "rejected";
            data: TGLLineItem[] | null;
            error: Error | null;
        }>({ status: "idle", data: null, error: null });

        const [meta, setMeta] = useState<{
            total: number;
            totalUnfiltered: number;
            pageSize: number;
        }>({ total: 0, totalUnfiltered: 0, pageSize: GL_LINES_PAGE_SIZE });

        const [facets, setFacets] = useState<TApiLineItemFilterOptions | null>(null);

        const loadFacets = useCallback(async (fileId: string) => {
            try {
                setFacets(
                    await api.files.getLineItemFilterOptions(fileId, { glGeneratedOnly: true }),
                );
            } catch {
                setFacets(null);
            }
        }, []);

        const fetchLines = useCallback(
            async (fileId: string, query: TApiLineItemQuery, signal?: AbortSignal) => {
                setGLLinesState((prev) => ({
                    status: "pending",
                    data: prev.data,
                    error: null,
                }));
                try {
                    const response = await api.files.getLineItems(
                        fileId,
                        {
                            ...query,
                            gl_generated_only: true,
                            limit: GL_LINES_PAGE_SIZE,
                        },
                        signal,
                    );
                    setMeta({
                        total: response.total,
                        totalUnfiltered: response.total_unfiltered ?? response.total,
                        pageSize: response.page_size,
                    });
                    setGLLinesState({
                        status: "fulfilled",
                        data: response.items.map((item) => ({
                            id: item.id,
                            lineNumber: item.line_number,
                            trannum: item.trannum,
                            accountCode: item.account_code,
                            accountName: item.account_name,
                            mappedAccountCode: item.mapped_account_code,
                            propertyCode: item.property_code,
                            mappedPropertyCode: item.mapped_property_code,
                            amount: item.amount,
                            debit: item.debit,
                            credit: item.credit,
                            transactionDate: item.transaction_date,
                            postingDate: item.posting_date,
                            bookNumber: item.book_number,
                            description: item.description,
                            reference: item.reference,
                            segment1: item.segment_1,
                            segment2: item.segment_2,
                            segment3: item.segment_3,
                            segment4: item.segment_4,
                            segment5: item.segment_5,
                            segment6: item.segment_6,
                            segment7: item.segment_7,
                            isGLGeneratedLine: item.is_gl_generated_line,
                        })),
                        error: null,
                    });
                } catch (err) {
                    // Swallow abort errors — the superseding request owns the loading state.
                    if (err instanceof DOMException && err.name === "AbortError") return;
                    setGLLinesState({
                        status: "rejected",
                        data: null,
                        error: err instanceof Error ? err : new Error("Failed to load GL lines"),
                    });
                }
            },
            [],
        );

        return {
            loadFacets,
            fetchLines,
            useGLLinesState: () => glLinesState as TAsyncState<TGLLineItem[]>,
            useGLLinesMeta: () => meta,
            useFacets: () => facets,
        };
    };

    /** The review page's Generated GL view, bound to its data. */
    const GeneratedGLTab = ({ file }: { file: TGLFileDetails }) => {
        const useCases = useGeneratedGLUseCases();
        return <GeneratedGLTabView file={file} useCases={useCases} />;
    };

    /**
     * The generated GL lines used to be their own page. They now live on the review
     * page, so the old address forwards there to keep bookmarks and shared links working.
     */
    const GLLinesRedirect = () => {
        const { fileId } = useParams<{ fileId: string }>();
        if (!fileId) {
            return <div>File ID is required</div>;
        }
        return <Navigate to={`${routerPaths.files.detail(fileId)}?tab=generated-gl`} replace />;
    };

    return { GeneratedGLTab, GLLinesRedirect };
};
